import axios from '../axiosInstance';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

// ============================================================================
// Types
// ============================================================================

export interface AlertRule {
  id: number;
  name: string;
  alert_name: string;
  expr: string;
  for_duration: string;
  severity: string;
  summary_template: string | null;
  description_template: string | null;
  enabled: boolean;
  exporter_type: string | null;
  exporter_id: number | null;
  category_id: number | null;
  customer_id: number | null;
  threshold_value: number | null;
  threshold_operator: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface RuleTemplate {
  id: number;
  name: string;
  category: string;
  exporter_type: string | null;
  variants: RuleTemplateVariant[];
}

export interface RuleTemplateVariant {
  id: number;
  template_id: number;
  severity: string;
  expr: string;
  for_duration: string;
  threshold_value: number | null;
  threshold_operator: string | null;
  summary_template: string | null;
  description_template: string | null;
}

export interface RuleFilters {
  enabled?: boolean;
  severity?: string;
  category_id?: number;
  is_system?: boolean;
  search?: string;
  exporter_type?: string;
}

export interface TemplateFilters {
  category?: string;
  exporter_type?: string;
}

export interface ValidateResult {
  valid: boolean;
  error?: string;
}

export interface PreviewResult {
  matches: number;
  series: Array<{
    labels: Record<string, string>;
    value: number;
  }>;
}

export interface MetricInfo {
  name: string;
  type?: string;
  help?: string;
}

export interface SyncResult {
  success: boolean;
  rules_synced: number;
  message?: string;
}

export interface CreateRuleData {
  name: string;
  expr: string;
  for_duration?: string;
  severity?: string;
  summary_template?: string;
  description_template?: string;
  enabled?: boolean;
  exporter_type?: string;
  exporter_id?: number;
  category_id?: number;
  threshold_value?: number;
  threshold_operator?: string;
}

export interface UpdateRuleData extends Partial<CreateRuleData> {}

// ============================================================================
// API Helper
// ============================================================================

/**
 * Helper function to determine API URL based on environment
 */
const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense/rules${endpoint}`;
};

// ============================================================================
// Rule Service
// ============================================================================

/**
 * Rule Service class - handles all alert rule CRUD and management API calls
 */
export class RuleService {
  async getRules(filters?: RuleFilters): Promise<{ rules: AlertRule[]; total: number }> {
    const API_URL = getApiUrl('');
    const response = await axios.get(API_URL, {
      params: filters,
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getRule(id: number): Promise<AlertRule> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async createRule(data: CreateRuleData): Promise<AlertRule> {
    const API_URL = getApiUrl('');
    const response = await axios.post(API_URL, data, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async updateRule(id: number, data: UpdateRuleData): Promise<AlertRule> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.put(API_URL, data, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async deleteRule(id: number): Promise<{ message: string }> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.delete(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async toggleRule(id: number): Promise<AlertRule> {
    const API_URL = getApiUrl(`/${id}/toggle`);
    const response = await axios.post(API_URL, {}, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getTemplates(params?: TemplateFilters): Promise<RuleTemplate[]> {
    const API_URL = getApiUrl('/templates');
    const response = await axios.get(API_URL, {
      params,
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    const data = response.data?.data || response.data;
    return Array.isArray(data) ? data : data?.templates || [];
  }

  async validatePromQL(expr: string): Promise<ValidateResult> {
    const API_URL = getApiUrl('/validate');
    const response = await axios.post(API_URL, { expr }, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async previewRule(expr: string, thresholdValue?: number): Promise<PreviewResult> {
    const API_URL = getApiUrl('/preview');
    const response = await axios.post(API_URL, {
      expr,
      threshold_value: thresholdValue,
    }, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getMetrics(search?: string, limit?: number): Promise<MetricInfo[]> {
    const API_URL = getApiUrl('/metrics');
    const response = await axios.get(API_URL, {
      params: { search, limit },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async syncRules(): Promise<SyncResult> {
    const API_URL = getApiUrl('/sync');
    const response = await axios.post(API_URL, {}, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }
}

// Export singleton instance
export const ruleService = new RuleService();

// Export with shorthand name
export const ruleApi = ruleService;
